import React from "react";
import useReveal from "../hooks/useReveal";

export default function About() {
  const [ref, visible] = useReveal();
  return (
    <section
      id="ueber-uns"
      ref={ref}
      data-testid="about-section"
      className={`relative bg-black border-t border-zinc-900 transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-12 py-24 md:py-32 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Image */}
        <div className="lg:col-span-6 relative">
          <div className="relative overflow-hidden border border-zinc-800 group">
            {/* The real photo with editorial treatment */}
            <img
              src="https://customer-assets.emergentagent.com/job_neuss-auto-repair/artifacts/o4noka3a_image.png"
              alt="Autohaus Barwinski – Filiale Neuss, Normannenstraße 22"
              className="w-full h-[420px] md:h-[560px] object-cover transition-transform duration-[1500ms] group-hover:scale-[1.03]"
              style={{
                objectPosition: "55% 40%",
                filter:
                  "grayscale(0.3) contrast(1.08) brightness(0.92) saturate(1.05)",
              }}
            />
            {/* Dark base gradient (top→bottom) — milder for brighter overall feel */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />
            {/* Diagonal yellow brand sheen */}
            <div className="absolute inset-0 bg-gradient-to-tr from-[#E0B33D]/12 via-transparent to-transparent mix-blend-overlay pointer-events-none" />
            {/* Vignette — softened */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.4) 100%)",
              }}
            />
            {/* Grain */}
            <div className="absolute inset-0 grain pointer-events-none" />

            {/* Top-left corner badge */}
            <div className="absolute top-6 left-6 border border-[#E0B33D] gold-text px-3 py-1 text-[10px] tracking-[0.3em] uppercase backdrop-blur-sm bg-black/30">
              Filiale Neuss
            </div>

            {/* Bottom-left caption */}
            <div className="absolute bottom-6 left-6 right-24 text-white">
              <div className="font-display uppercase tracking-tight text-xl md:text-2xl leading-tight">
                Normannenstraße 22
              </div>
              <div className="text-[11px] tracking-[0.3em] uppercase text-zinc-300 mt-1">
                41462 Neuss · seit 2024 Spitzer und Demirci GmbH
              </div>
            </div>

            {/* Yellow accent corner bracket bottom-right */}
            <div className="absolute bottom-0 right-0 w-20 h-20 pointer-events-none">
              <div className="absolute right-0 bottom-0 w-full h-1 gold-bg" />
              <div className="absolute right-0 bottom-0 w-1 h-full gold-bg" />
            </div>
          </div>

          {/* Accent badge — moved below the image so it does not block the photo */}
          <div className="mt-6 inline-flex items-center gap-5 gold-bg text-black px-6 py-4">
            <div className="font-display text-4xl leading-none">4,7</div>
            <div className="w-px h-9 bg-black/30" />
            <div className="text-[10px] tracking-[0.25em] uppercase leading-tight">
              Google Bewertung
              <br /> 119+ Rezensionen
            </div>
          </div>
        </div>

        {/* Text */}
        <div className="lg:col-span-6">
          <div className="text-[11px] tracking-[0.4em] uppercase gold-text">
            — Über uns
          </div>
          <h2
            data-testid="about-heading"
            className="font-display mt-4 text-4xl md:text-6xl uppercase tracking-tight leading-[0.95]"
          >
            Neue Meister.
            <br />
              <span className="gold-text">Alter Name.</span>
            <br />
            Volles Herz.
          </h2>

          <div
            className="mt-6 space-y-4 text-zinc-300 text-[15px] leading-relaxed max-w-xl"
            style={{ wordSpacing: "0.35em" }}
          >
            <p>
              Seit <strong className="text-white">2024</strong> wird das
              traditionsreiche Autohaus Barwinski von den Kfz-Meistern{" "}
              <strong className="gold-text">Vitali Spitzer</strong> und{" "}
              <strong className="gold-text">Taner Demirci</strong>{" "}
              geführt — als <em>Autohaus Barwinski by Spitzer und Demirci GmbH</em>.
              Beide Geschäftsführer sind selbst Meister und bringen jeweils{" "}
              <strong className="text-white">über 30 Jahre Erfahrung</strong>{" "}
              mit. Unser Werkstattmeister{" "}
              <strong className="text-white">Miroslaw Kurylo</strong> ergänzt
              das Team mit{" "}
              <strong className="text-white">40 Jahren Renault-Erfahrung</strong>{" "}
              — eine Tiefe, die man nicht ersetzen kann.
            </p>
            <p>
              Wir nehmen uns für jede Beratung die Zeit, die sie braucht. Egal
              ob kurze Frage am Telefon oder ausführliches Gespräch in der
              Werkstatt — bei uns sind Sie nie ein Auftrag, sondern ein Mensch
              mit einem Auto.
            </p>
          </div>

          <a
            href="#termin"
            data-testid="about-cta"
            className="mt-10 inline-flex items-center gap-2 gold-bg text-black font-semibold px-6 py-3 text-sm tracking-[0.25em] uppercase transition-colors"
          >
            Termin vereinbaren
          </a>
        </div>
      </div>
    </section>
  );
}
